import { Collection } from 'firestorter';

import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';
import { workoutAssignmentStatuses } from '../../Model/WorkoutAssignment';

const WorkoutFeedViewType = {
  COACH_ASSIGNED_WORKOUT: 'coach_assigned_workout',
  SELF_ASSIGNED_WORKOUT: 'self_assigned_workout',
  CUSTOM_WORKOUT: 'custom_workout',
  TRACKED_ACTIVITY_SUMMARY: 'tracked_activity_summary',
  TRACKED_ACTIVITY: 'tracked_activity',
  CHECK_IN: 'check_in',
};

class WorkoutFeedView extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.WORKOUT_FEED_VIEW}/${id}`, opts);
  }

  get title() {
    return this.data.title;
  }

  get coach() {
    return this.data.coach;
  }

  // The client's health data for this workout, once the mobile health sync links it.
  get trackedActivity() {
    return this.data.trackedActivity;
  }

  get user() {
    return this.data.user;
  }

  get startDate() {
    return this.data.startDate?.toDate();
  }

  get endDate() {
    return this.data.endDate?.toDate();
  }

  get status() {
    return this.data.status;
  }

  get type() {
    return this.data.type;
  }

  get duration() {
    return this.data.duration;
  }

  get comment() {
    return this.data.comment;
  }

  // A client's feed rows starting within the range, inclusive.
  static async getByUserInDateRange(userId, startDate, endDate) {
    const collection = new Collection(() => Collections.WORKOUT_FEED_VIEW, {
      createDocument: (src, opts) => new WorkoutFeedView(src.id, opts),
      query: (ref) => ref
        .where('user', '==', userId)
        .where('startDate', '>=', startDate)
        .where('startDate', '<=', endDate)
        .orderBy('startDate', 'asc'),
    });
    await collection.fetch();
    return collection.docs;
  }

  /**
   * A coach's view of a client's completed history: their own programming, plus everything the
   * client recorded themselves, which every coach sees.
   * @param {string} userId The client id
   * @param {string} coachId The coach whose programming to include
   * @param {number} limit How many rows to return
   * @returns {Promise<{ docs: WorkoutFeedView[] }>} The newest `limit` rows, newest first
   */
  static async getCompletedWorkoutHistoryByUser(userId, coachId, limit) {
    const newestFirst = (narrow) => new Collection(() => Collections.WORKOUT_FEED_VIEW, {
      createDocument: (src, opts) => new WorkoutFeedView(src.id, opts),
      query: (ref) => narrow(ref
        .where('user', '==', userId)
        .where('status', '==', workoutAssignmentStatuses.COMPLETED))
        .orderBy('startDate', 'desc')
        .limit(limit),
    });

    const coachAssigned = newestFirst((ref) => ref
      .where('type', '==', WorkoutFeedViewType.COACH_ASSIGNED_WORKOUT)
      .where('coach', '==', coachId));

    const clientRecorded = newestFirst((ref) => ref
      .where('type', 'in', [
        WorkoutFeedViewType.SELF_ASSIGNED_WORKOUT,
        WorkoutFeedViewType.CUSTOM_WORKOUT,
        WorkoutFeedViewType.TRACKED_ACTIVITY,
      ]));

    await Promise.all([coachAssigned.fetch(), clientRecorded.fetch()]);

    const docs = [...coachAssigned.docs, ...clientRecorded.docs]
      .sort((a, b) => (b.startDate?.getTime() ?? 0) - (a.startDate?.getTime() ?? 0))
      .slice(0, limit);

    return { docs };
  }
}

export default WorkoutFeedView;
